import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './components/Login';
import Navigation from './components/Navigation';
import RBMSHistoricData from './components/RBMSHistoricData';
import ModuleHistoricData from './components/ModuleHistoricData';
import GroupSelector from './components/GroupSelector';
import CompareSelector from './components/CompareSelector';
import CompareView from './components/CompareView';
import EnergyOverview from './components/EnergyOverview';
import Dashboard from './components/Dashboard';
import WidgetContainer from './components/WidgetContainer';
import { parseAssetModel, getBatterySlaveIds } from './utils/assetConfig';
import { buildBatteryOptions } from './utils/compareSelection';
import { canAccessGroups, fetchOwnDevices } from './utils/userDevices';

function App() {
  const [activeView, setActiveView] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authData, setAuthData] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [groupDevices, setGroupDevices] = useState([]);
  const [ownDevices, setOwnDevices] = useState([]);
  const [compareSelection, setCompareSelection] = useState([]);
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const stored = localStorage.getItem('authData');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        data.role = Number(data.role);
        // Check if token is expired
        const expiryDate = new Date(data.expires);
        if (expiryDate > new Date()) {
          setAuthData(data);
          setIsAuthenticated(true);
        } else {
          // Token expired, clear storage
          localStorage.removeItem('authData');
        }
      } catch {
        localStorage.removeItem('authData');
      }
    }
  }, []);

  // Fetch own devices for users without group access
  useEffect(() => {
    if (!isAuthenticated || !authData?.token) return;
    if (canAccessGroups(authData.role)) return;   // dealers keep the group flow
    let cancelled = false;
    fetchOwnDevices(authData.token, authData.role).then((devs) => {
      if (!cancelled) setOwnDevices(devs);
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, authData]);

  const handleLogin = (data) => {
    setAuthData(data);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authData');
    setAuthData(null);
    setIsAuthenticated(false);
    setSelectedDevice(null);
    setGroupDevices([]);
    setOwnDevices([]);
    setCompareSelection([]);
    setActiveView('live');
  };

  const handleDeviceSelected = (device, allDevices) => {
    setSelectedDevice(device);
    if (allDevices) {
      setGroupDevices(allDevices);
    }
  };

  const handleSwitchDevice = (deviceId) => {
    const device = groupDevices.find(d => d.deviceId === deviceId);
    if (device) {
      setSelectedDevice(device);
    }
  };

  const handleChangeGroup = () => {
    setSelectedDevice(null);
    setGroupDevices([]);
    setCompareSelection([]);
  };

  const deviceListForCompare =
      groupDevices.length > 0 ? groupDevices
    : ownDevices.length   > 0 ? ownDevices
    : (authData?.devices || []);

  useEffect(() => {
    if (isAuthenticated && deviceListForCompare.length > 0 && !hasAutoSelected) {
      const options = buildBatteryOptions(deviceListForCompare);
      setCompareSelection(options);
      setHasAutoSelected(true);
    }
  }, [isAuthenticated, deviceListForCompare, hasAutoSelected]);

  useEffect(() => {
    setHasAutoSelected(false);
  }, [deviceListForCompare]);

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // Role 2 (admin) - authenticated but no device selected yet
  // (except in views that operate on the whole device list: live, dashboard, widgets)
  if (authData.role === 2 && !selectedDevice && activeView !== 'live' && activeView !== 'dashboard' && activeView !== 'widgets') {
    return (
      <div className="App">
        <div className="app-layout">
          <Navigation
            activeView={activeView}
            setActiveView={setActiveView}
            onLogout={handleLogout}
            userInfo={{
              firstName: authData?.profile?.firstName,
              lastName: authData?.profile?.lastName,
              username: authData?.username
            }}
            role={authData?.role}
          />
          <main className="main-content">
            <GroupSelector authData={authData} onDeviceSelected={handleDeviceSelected} />
          </main>
        </div>
      </div>
    );
  }

  // Authenticated + device selected (non-group users always fall back to own devices; group users after device selection)
  const effectiveDevice = selectedDevice || deviceListForCompare[0] || null;

  const assetConfig = effectiveDevice ? parseAssetModel(effectiveDevice.carProfile?.model) : null;
  const batterySlaveIds = assetConfig ? getBatterySlaveIds(assetConfig) : [];

  return (
    <div className="App">
      <div className="app-layout">
        <Navigation
          activeView={activeView}
          setActiveView={setActiveView}
          onLogout={handleLogout}
          userInfo={{
            firstName: authData?.profile?.firstName,
            lastName: authData?.profile?.lastName,
            username: authData?.username
          }}
          selectedDevice={effectiveDevice}
          groupDevices={deviceListForCompare}
          onSwitchDevice={handleSwitchDevice}
          onChangeGroup={authData.role === 2 ? handleChangeGroup : null}
          role={authData?.role}
        />
        <main className="main-content">
          {activeView === 'dashboard' && (
            <Dashboard
              authData={authData}
              devices={deviceListForCompare}
            />
          )}
          {activeView === 'rbms' && (
            <RBMSHistoricData
              authData={authData}
              selectedDevice={effectiveDevice}
              assetConfig={assetConfig}
              batterySlaveIds={batterySlaveIds}
              groupDevices={deviceListForCompare}
            />
          )}
          {activeView === 'module' && (
            <ModuleHistoricData
              authData={authData}
              selectedDevice={effectiveDevice}
              assetConfig={assetConfig}
              batterySlaveIds={batterySlaveIds}
              groupDevices={deviceListForCompare}
            />
          )}
          {activeView === 'live' && (
            deviceListForCompare.length === 0 ? (
              <GroupSelector
                authData={authData}
                onDeviceSelected={handleDeviceSelected}
                onGroupSelected={(allDevices) => setGroupDevices(allDevices)}
              />
            ) : (
              compareSelection.length === 0 ? (
                <CompareSelector
                  devices={deviceListForCompare}
                  onCompareSelected={setCompareSelection}
                  onChangeGroup={authData.role === 2 ? handleChangeGroup : null}
                />
              ) : (
                <CompareView
                  authData={authData}
                  compareSelection={compareSelection}
                  onChangeSelection={() => setCompareSelection([])}
                />
              )
            )
          )}
          {activeView === 'energy' && (
            <EnergyOverview
              authData={authData}
              selectedDevice={effectiveDevice}
            />
          )}
          {activeView === 'widgets' && (
            <WidgetContainer
              authData={authData}
              devices={deviceListForCompare}
            />
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
